import React, { useEffect, useMemo, useState } from 'react';

import { Check, Flag, X } from 'lucide-react';

import { useCountries } from '../../hooks/useCountryApi';
import { useRequestCourse } from '../../hooks/useCourseRequestApi';
import { useStates } from '../../hooks/useStateApi';
import PlaceSelect from '../maps/PlaceSelect';

// Same dark-panel field styling as MemberSetupDrawer, so the two drawers read as one product.
const fieldClasses =
  'h-11 w-full rounded-lg border border-primary/25 bg-black/30 px-3 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-60';

const labelClasses =
  'mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/50';

// Matches CourseRequest.CourseName on the backend, which matches Course.Name.
const COURSE_NAME_MAX_LENGTH = 100;

const messageFrom = (err: unknown, fallback: string): string => {
  const failure = err as {
    message?: unknown;
    result?: { message?: unknown };
  } | null;
  const apiMessage = failure?.result?.message ?? failure?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
    return apiMessage;
  }
  return fallback;
};

// PlaceSelect keys and picks by name, and the lookup has the odd name twice (Vanuatu, from the
// legacy data), so only the first of each name is offered.
const uniqueByName = <T extends { name: string }>(rows: T[]): T[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = row.name.trim().toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

interface CourseRequestFormProps {
  initialCourseName: string;
  onDone: () => void;
}

// Split out from the shell so it only mounts once the drawer has been opened. The shell is on the
// page from first load, for every visitor, and the country list needs a token: fetching it for a
// signed-out visitor would 401, and the axios interceptor answers a 401 by sending them to /login.
// The drawer is only ever opened past the member gate, so by the time this mounts there is a token.
const CourseRequestForm: React.FC<CourseRequestFormProps> = ({
  initialCourseName,
  onDone,
}) => {
  const [courseName, setCourseName] = useState(initialCourseName);
  const [countryId, setCountryId] = useState('');
  const [stateId, setStateId] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSent, setIsSent] = useState(false);

  const { data: countries = [] } = useCountries();
  const { data: states = [] } = useStates();

  // Every country in the lookup, not only the ones the map has courses in: the course being asked
  // for may well be the first one in its country.
  const countryOptions = useMemo(
    () =>
      uniqueByName(
        countries
          .filter((c) => c.id && c.name)
          .map((c) => ({ id: c.id as string, name: c.name as string })),
      ),
    [countries],
  );

  const stateOptions = useMemo(
    () =>
      countryId
        ? uniqueByName(
            states
              .filter(
                (st) =>
                  st.id &&
                  st.name &&
                  st.countryId?.toLowerCase() === countryId.toLowerCase(),
              )
              .map((st) => ({ id: st.id as string, name: st.name as string }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          )
        : [],
    [states, countryId],
  );

  // PlaceSelect works in names (the filter drawer only has names), so the ids are looked up here.
  const countryName =
    countryOptions.find((c) => c.id === countryId)?.name ?? null;
  const stateName = stateOptions.find((st) => st.id === stateId)?.name ?? null;
  const requestCourse = useRequestCourse();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    try {
      await requestCourse.mutateAsync({
        courseName: courseName.trim(),
        countryId: countryId || undefined,
        stateId: stateId || undefined,
        websiteUrl: websiteUrl.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setIsSent(true);
    } catch (err: unknown) {
      setError(
        messageFrom(err, 'Could not send that request. Please try again.'),
      );
    }
  };

  if (isSent) {
    return (
      <div className="flex flex-col items-start">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Check className="h-5 w-5" />
        </span>
        <p className="mt-4 text-base font-semibold text-white">
          Thanks, we are on it
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-white/50">
          We will look into {courseName.trim()} and email you as soon as it is
          on GolfPlayed.
        </p>
        <button
          type="button"
          onClick={onDone}
          className="mt-6 h-11 w-full rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <>
      <p className="text-base font-semibold text-white">
        Can&apos;t find your course?
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-white/50">
        Tell us about it and we will add it. The more you can tell us, the
        quicker it goes.
      </p>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
        >
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div>
          <label htmlFor="course-request-name" className={labelClasses}>
            Course name
          </label>
          <input
            id="course-request-name"
            type="text"
            required
            maxLength={COURSE_NAME_MAX_LENGTH}
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            placeholder="e.g. Fancourt Links"
            className={fieldClasses}
          />
        </div>

        <div>
          <span className={labelClasses}>Country</span>
          {/* The same picker as the map's filter drawer, so choosing a place reads the same on both. */}
          <PlaceSelect
            label="country"
            placeholder="Not sure"
            allLabel="Not sure"
            options={countryOptions}
            value={countryName}
            // A state from the country before is not in the new country's list, so it goes too.
            onChange={(next) => {
              setCountryId(
                countryOptions.find((c) => c.name === next)?.id ?? '',
              );
              setStateId('');
            }}
            kind="country"
            searchable
          />
        </div>

        <div>
          <span className={labelClasses}>State or province</span>
          <PlaceSelect
            label="state or province"
            placeholder={countryId ? 'Not sure' : 'Pick a country first'}
            allLabel="Not sure"
            options={stateOptions}
            value={stateName}
            onChange={(next) =>
              setStateId(stateOptions.find((st) => st.name === next)?.id ?? '')
            }
            kind="region"
            disabled={!countryId || stateOptions.length === 0}
            searchable={stateOptions.length > 10}
          />
        </div>

        <div>
          <label htmlFor="course-request-website" className={labelClasses}>
            Website (optional)
          </label>
          <input
            id="course-request-website"
            type="text"
            inputMode="url"
            maxLength={255}
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="www.example.co.za"
            className={fieldClasses}
          />
        </div>

        <div>
          <label htmlFor="course-request-notes" className={labelClasses}>
            Anything else (optional)
          </label>
          <textarea
            id="course-request-notes"
            rows={3}
            maxLength={1000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={`${fieldClasses} h-auto py-2`}
          />
        </div>

        <button
          type="submit"
          disabled={requestCourse.isPending || courseName.trim().length === 0}
          className="h-11 w-full rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {requestCourse.isPending ? 'Sending...' : 'Send request'}
        </button>
      </form>
    </>
  );
};

interface CourseRequestDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  // What the member had typed into the search box, so they do not have to type it twice.
  initialCourseName: string;
}

// Asking for a course that is not on GolfPlayed. Only ever opened past Explore's member gate.
//
// Same shell as MemberSetupDrawer: right-hand side, stays mounted and parks off-screen so it
// animates both ways, aria-hidden and pointer-events keeping it out of reach while shut.
const CourseRequestDrawer: React.FC<CourseRequestDrawerProps> = ({
  isOpen,
  onClose,
  initialCourseName,
}) => {
  // Bumped on every open, and used as the form's key, so each request starts clean with the
  // latest search term rather than showing the last one's "Thanks" or its half-typed fields.
  const [openCount, setOpenCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setOpenCount((n) => n + 1);
    }
  }, [isOpen]);

  // Escape closes it, same as every other drawer on this page. Only bound while it is up.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label="Request a course"
      className={`fixed inset-y-0 right-0 z-[60] flex w-full flex-col border-l border-primary/20 bg-charcoal shadow-2xl transition-transform duration-300 ease-in-out sm:w-96 ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white">
          <Flag className="h-4 w-4 text-primary" />
          Request a course
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-8 pt-5">
        {openCount > 0 && (
          <CourseRequestForm
            key={openCount}
            initialCourseName={initialCourseName}
            onDone={onClose}
          />
        )}
      </div>
    </aside>
  );
};

export default CourseRequestDrawer;
